import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CreditCardIcon, CheckIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
const MAIN_PRICE = 10000;
export function PricingCard() {
  const [openItem, setOpenItem] = useState<string | null>('package-1');
  const [showBankDetails, setShowBankDetails] = useState(false);
  const toggleItem = (id: string) => {
    setOpenItem(openItem === id ? null : id);
  };
  return <section id="pricing" className="py-20 md:py-28 px-4 md:px-6 border-b border-white/[0.06]" data-id="element-99">
      <div className="max-w-5xl mx-auto" data-id="element-100">
        <ScrollReveal data-id="element-101">
          <div className="text-center mb-12 md:mb-16" data-id="element-102">
            <span className="text-accent font-bold tracking-[0.2em] text-xs md:text-sm uppercase block mb-4" data-id="element-103">
              Pricing
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight" data-id="element-104">
              Investment
            </h2>
          </div>
        </ScrollReveal>

        <div className="space-y-4" data-id="element-105">
          {/* PACKAGE 1: Main Project */}
          <ScrollReveal direction="up" data-id="element-106">
            <div className={`bg-surface border transition-colors duration-300 overflow-hidden ${openItem === 'package-1' ? 'border-accent/50 shadow-[0_0_30px_-10px_rgba(255,107,53,0.15)]' : 'border-white/[0.06] hover:border-white/[0.1]'}`} data-id="element-107">
              <button onClick={() => toggleItem('package-1')} className="w-full flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 text-left gap-4 relative" data-id="element-108">
                {openItem === 'package-1' && <motion.div layoutId="active-border" className="absolute top-0 left-0 w-1 h-full bg-accent" data-id="element-109" />}
                <div className="flex-1 pl-2 md:pl-0" data-id="element-110">
                  <div className="flex items-center gap-3 mb-2" data-id="element-111">
                    <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight" data-id="element-112">
                      Website & Brand Development
                    </h3>
                    <span className="bg-accent text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest rounded-sm" data-id="element-113">
                      Recommended
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm md:text-base font-light" data-id="element-114">
                    A complete website and brand development project to position
                    Accutemp as the trusted commercial HVAC partner.
                  </p>
                </div>
                <div className="flex items-center justify-between md:justify-end gap-6 pl-2 md:pl-0 w-full md:w-auto" data-id="element-115">
                  <div className="text-left md:text-right" data-id="element-116">
                    <div className="text-xl md:text-2xl font-black text-white tracking-tight" data-id="element-117">
                      £{MAIN_PRICE.toLocaleString()}{' '}
                      <span className="text-sm font-normal text-zinc-500" data-id="element-118">
                        + VAT
                      </span>
                    </div>
                    <div className="text-zinc-500 font-mono text-xs uppercase tracking-wider" data-id="element-119">
                      6 weeks duration
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${openItem === 'package-1' ? 'rotate-180 text-accent' : ''}`} data-id="element-120" />
                </div>
              </button>

              <AnimatePresence data-id="element-121">
                {openItem === 'package-1' && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-122">
                    <div className="px-6 md:px-8 pb-8 md:pb-12 border-t border-white/[0.06] pt-8" data-id="element-123">
                      <div className="mb-10 max-w-3xl" data-id="element-124">
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed font-light" data-id="element-125">
                          Built to strengthen authority, improve lead quality
                          and reduce reliance on price-based competition. By the
                          end of 6 weeks you will have a confident digital
                          presence ready to generate qualified enquiries.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 mb-12" data-id="element-126">
                        <div data-id="element-127">
                          <h4 className="text-xs font-bold text-accent tracking-[0.2em] uppercase mb-6" data-id="element-128">
                            Expected outcomes
                          </h4>
                          <ul className="space-y-4" data-id="element-129">
                            {['Stronger professional image', 'Higher quality enquiries', 'Reduced price sensitivity', 'Increased direct commercial contracts', 'Long term digital assets generating leads'].map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-300" data-id="element-130">
                                <CheckIcon className="w-4 h-4 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-131" />
                                {item}
                              </li>)}
                          </ul>
                        </div>

                        <div data-id="element-132">
                          <h4 className="text-xs font-bold text-accent tracking-[0.2em] uppercase mb-6" data-id="element-133">
                            Investment & Payment Terms
                          </h4>
                          <div className="bg-surface-elevated border border-white/[0.06] p-6 md:p-8 mb-6" data-id="element-134">
                            <div className="text-2xl font-black text-white mb-6" data-id="element-135">
                              £10,000{' '}
                              <span className="text-sm font-normal text-zinc-500" data-id="element-136">
                                + VAT
                              </span>
                            </div>
                            <div className="space-y-5" data-id="element-137">
                              <div className="flex items-start" data-id="element-138">
                                <div className="w-8 h-8 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mr-4 flex-shrink-0 text-accent font-mono text-xs font-bold" data-id="element-139">
                                  1
                                </div>
                                <div data-id="element-140">
                                  <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-141">
                                    50% Upfront
                                  </div>
                                  <div className="text-zinc-400 text-xs font-mono" data-id="element-142">
                                    £5,000 + VAT • Due to secure the project
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-start" data-id="element-143">
                                <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mr-4 flex-shrink-0 text-zinc-400 font-mono text-xs font-bold" data-id="element-144">
                                  2
                                </div>
                                <div data-id="element-145">
                                  <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-146">
                                    25% Design Approval
                                  </div>
                                  <div className="text-zinc-400 text-xs font-mono" data-id="element-147">
                                    £2,500 + VAT • Upon approval of chosen
                                    website design direction
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-start" data-id="element-148">
                                <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mr-4 flex-shrink-0 text-zinc-400 font-mono text-xs font-bold" data-id="element-149">
                                  3
                                </div>
                                <div data-id="element-150">
                                  <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-151">
                                    25% Completion
                                  </div>
                                  <div className="text-zinc-400 text-xs font-mono" data-id="element-152">
                                    £2,500 + VAT • Upon completion prior to
                                    launch
                                  </div>
                                </div>
                              </div>
                            </div>
                            <p className="mt-5 text-[10px] text-zinc-600 uppercase tracking-widest border-t border-white/[0.06] pt-4" data-id="element-153">
                              Work begins once initial payment is received.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-154">
                        <button onClick={e => {
                      e.stopPropagation();
                      setShowBankDetails(!showBankDetails);
                    }} className="flex items-center text-xs md:text-sm font-bold text-zinc-400 hover:text-white transition-colors uppercase tracking-widest" data-id="element-155">
                          <CreditCardIcon className="w-4 h-4 mr-3" data-id="element-156" />
                          {showBankDetails ? 'Hide bank details' : 'Show bank details'}
                          <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform ${showBankDetails ? 'rotate-180' : ''}`} data-id="element-157" />
                        </button>

                        <AnimatePresence data-id="element-158">
                          {showBankDetails && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden" data-id="element-159">
                              <div className="mt-6 p-6 bg-[#050507] text-white border border-white/[0.06]" data-id="element-160">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm" data-id="element-161">
                                  <div data-id="element-162">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-163">
                                      Bank
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-164">
                                      Clearbank
                                    </span>
                                  </div>
                                  <div data-id="element-165">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-166">
                                      Account name
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-167">
                                      AVY Consultancy Limited
                                    </span>
                                  </div>
                                  <div data-id="element-168">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-169">
                                      Company number
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-170">
                                      12143361
                                    </span>
                                  </div>
                                  <div data-id="element-171">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-172">
                                      Sort code
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-173">
                                      04-06-05
                                    </span>
                                  </div>
                                  <div data-id="element-174">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-175">
                                      Account no
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-176">
                                      16926743
                                    </span>
                                  </div>
                                  <div data-id="element-177">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-178">
                                      Reference
                                    </span>
                                    <span className="font-mono text-accent" data-id="element-179">
                                      ACCUTEMP-WEB-2026
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </ScrollReveal>

          {/* ONGOING PACKAGES */}
          {[{
          id: 'package-2',
          title: 'Website Maintenance',
          price: '£350/mo',
          duration: 'Ongoing monthly',
          description: 'Ongoing website care so you can focus on running your business. We keep things updated, secure and performing well.',
          deliverables: ['Website hosting', 'Security updates', 'Technical maintenance', 'Page content updates', 'Two blog articles per month', 'Minor design adjustments', 'Ongoing support', 'Quarterly performance review']
        }, {
          id: 'package-3',
          title: 'Ongoing SEO & Optimisation',
          price: '£200/mo',
          duration: 'Ongoing monthly',
          description: 'Continuous search optimisation to improve rankings, attract better enquiries and stay ahead of competitors.',
          deliverables: ['Ongoing keyword optimisation', 'Improvement of service page rankings', 'Blog optimisation', 'Local search improvements', 'Search performance monitoring', 'AI visibility refinement', 'Monthly implementation updates']
        }].map((pkg, index) => <ScrollReveal key={pkg.id} direction="up" delay={0.1 + index * 0.05} data-id="element-180">
              <div className={`bg-surface border transition-colors duration-300 overflow-hidden ${openItem === pkg.id ? 'border-white/[0.12]' : 'border-white/[0.06] hover:border-white/[0.1]'}`} data-id="element-181">
                <button onClick={() => toggleItem(pkg.id)} className="w-full flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 text-left gap-4" data-id="element-182">
                  <h3 className={`text-lg md:text-xl font-bold uppercase tracking-tight transition-colors ${openItem === pkg.id ? 'text-white' : 'text-zinc-400'}`} data-id="element-183">
                    {pkg.title}
                  </h3>
                  <div className="flex items-center gap-6" data-id="element-184">
                    <div className="text-left md:text-right" data-id="element-185">
                      <span className="text-lg md:text-xl font-black text-white tracking-tight" data-id="element-186">
                        {pkg.price}
                      </span>
                      <span className="text-sm font-normal text-zinc-500 ml-1" data-id="element-187">
                        + VAT
                      </span>
                      <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider" data-id="element-188">
                        {pkg.duration}
                      </div>
                    </div>
                    <ChevronDownIcon className={`w-5 h-5 text-zinc-500 transition-transform duration-300 flex-shrink-0 ${openItem === pkg.id ? 'rotate-180 text-white' : ''}`} data-id="element-189" />
                  </div>
                </button>
                <AnimatePresence data-id="element-190">
                  {openItem === pkg.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-191">
                      <div className="px-6 md:px-8 pb-8 border-t border-white/[0.06] pt-6" data-id="element-192">
                        <p className="text-zinc-300 text-sm md:text-base font-light leading-relaxed mb-6 max-w-2xl" data-id="element-193">
                          {pkg.description}
                        </p>
                        <h4 className="text-[10px] font-bold text-zinc-500 tracking-[0.2em] uppercase mb-4" data-id="element-194">
                          What's included
                        </h4>
                        <ul className="space-y-3" data-id="element-195">
                          {pkg.deliverables.map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-400" data-id="element-196">
                              <CheckIcon className="w-3.5 h-3.5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-197" />
                              {item}
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </ScrollReveal>)}
        </div>
      </div>
    </section>;
}